package io.horion.cv.components.forms

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import io.horion.cv.viewModel.CvViewModel
import io.horion.cv.components.widgets.DateRangeSelector
import io.horion.cv.components.widgets.FormActionButtons
import io.horion.cv.components.widgets.RichTextDescription
import io.horion.cv.components.widgets.TextFormField
import io.horion.cv.models.ExperienceState
import io.horion.cv.styles.ActivityFullWidthFieldStyle
import io.horion.cv.styles.ActivityHalfWidthFieldStyle
import org.jetbrains.compose.web.css.px

// Convertit le HTML produit par l'éditeur riche en liste de points.
// Avant : seule une regex sur <li> était utilisée, donc si l'utilisateur
// tapait du texte normal (sans cliquer sur "liste à puces"), aucun <li>
// n'existait dans le HTML -> la regex ne trouvait rien -> description
// enregistrée vide, même quand l'utilisateur avait bien tapé du texte.
//
// Maintenant :
// 1. Si l'utilisateur a fait une vraie liste à puces -> un point par <li>.
// 2. Sinon, si le HTML contient des paragraphes/blocs (<div>/<p>, ce que
//    produit un contentEditable pour du texte normal) -> un point par bloc.
// 3. Sinon (texte brut sans aucune balise de bloc) -> tout le HTML devient
//    un seul point, pour ne jamais perdre ce que l'utilisateur a tapé.
private fun htmlToDescriptionList(html: String): List<String> {
    if (html.isBlank()) return emptyList()

    val liMatches = Regex("<li[^>]*>([\\s\\S]*?)</li>")
        .findAll(html)
        .map { it.groupValues[1].trim() }
        .filter { it.isNotBlank() }
        .toList()
    if (liMatches.isNotEmpty()) return liMatches

    val blockMatches = Regex("<(?:div|p)[^>]*>([\\s\\S]*?)</(?:div|p)>")
        .findAll(html)
        .map { it.groupValues[1].trim() }
        .filter { it.isNotBlank() && it != "<br>" }
        .toList()
    if (blockMatches.isNotEmpty()) return blockMatches

    return listOf(html)
}

@Composable
fun ExperienceForm(
    index: Int,
    state: ExperienceState,
    cvViewModel: CvViewModel,
    onDelete: () -> Unit,
    onComplete: () -> Unit
) {

    val updateVM = {
        val descriptionList = htmlToDescriptionList(state.description)

        val updatedExp = io.horion.cv.api.datasource.cv.Experience(
            post = state.poste,
            enterprise = state.employeur,
            city = state.ville,
            started = "${state.moisDebut}-${state.anneeDebut}",
            ended = if (state.isOngoing) "En cours" else "${state.moisFin}-${state.anneeFin}",
            description = descriptionList
        )
        cvViewModel.updateExperience(index, updatedExp)
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(25.px)
    ) {
        TextFormField(
            label = "Poste",
            value = state.poste,
            onValueChange = {
                state.poste = it
                updateVM()
            },
            modifier = Modifier.fillMaxWidth()
        )

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .displayUntil(Breakpoint.MD)
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                TextFormField(
                    label = "Employeur",
                    value = state.employeur,
                    onValueChange = {
                        state.employeur = it
                        updateVM()
                    },
                    modifier = ActivityFullWidthFieldStyle.toModifier()
                )
                TextFormField(
                    label = "Ville",
                    value = state.ville,
                    onValueChange = {
                        state.ville = it
                        updateVM()
                    },
                    modifier = ActivityFullWidthFieldStyle.toModifier()
                )
            }
        }

        Box(modifier = Modifier
            .fillMaxWidth()
            .displayIfAtLeast(Breakpoint.MD)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(25.px)
            ) {
                TextFormField(
                    label = "Employeur",
                    value = state.employeur,
                    onValueChange = {
                        state.employeur = it
                        updateVM()
                    },
                    modifier = ActivityHalfWidthFieldStyle.toModifier()
                )
                TextFormField(
                    label = "Ville",
                    value = state.ville,
                    onValueChange = {
                        state.ville = it
                        updateVM()
                    },
                    modifier = ActivityHalfWidthFieldStyle.toModifier()
                )
            }
        }

        DateRangeSelector(
            startMonth = state.moisDebut,
            startYear = state.anneeDebut,
            endMonth = state.moisFin,
            endYear = state.anneeFin,
            isOngoing = state.isOngoing,
            onStartMonthChange = { state.moisDebut = it; updateVM() },
            onStartYearChange = { state.anneeDebut = it; updateVM() },
            onEndMonthChange = { state.moisFin = it; updateVM() },
            onEndYearChange = { state.anneeFin = it; updateVM() },
            onOngoingChange = {
                state.isOngoing = it
                if (it) {
                    state.moisFin = ""
                    state.anneeFin = ""
                }
                updateVM()
            }
        )

        RichTextDescription(
            label = "Description",
            value = state.description,
            onValueChange = {
                state.description = it
                updateVM()
            },
            modifier = Modifier.fillMaxWidth()
        )

        FormActionButtons(
            onDelete = onDelete,
            onComplete = onComplete
        )
    }
}